import { type ReactNode } from "react";
import Add from "@mui/icons-material/Add";
import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import ContactSupportOutlined from "@mui/icons-material/ContactSupportOutlined";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import GroupOutlined from "@mui/icons-material/GroupOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LogoutSharp from "@mui/icons-material/LogoutSharp";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import PaymentOutlined from "@mui/icons-material/PaymentOutlined";
import PeopleOutlineOutlined from "@mui/icons-material/PeopleOutlineOutlined";
import Person2Outlined from "@mui/icons-material/Person2Outlined";
import Settings from "@mui/icons-material/Settings";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import TimelineOutlined from "@mui/icons-material/TimelineOutlined";

import type { ExtendedThalamosUser } from "@thalamos/aspire-common";
import {
  canDoctorInitiateS12Payments,
  canViewPatientList,
  defaultConfiguration,
  hasDoctorGmcNumber,
  isCqcUserSession,
  isDemographicsManagerUser,
  isGuestUserSession,
  isManagerUser,
  isMcaUserSession,
  isNHSDashboardViewerUser,
  isPaymentReviewerSession,
  isSoadUserSession,
  isSupersetDashboardViewerUser,
  isTeamManagerUser,
} from "@thalamos/aspire-common";

import { config } from "../../../config.js";
import { routeFns } from "../../../routePaths.js";

type ThalamosMenuItems = {
  label: string;
  link?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon: ReactNode;
  text: string;
  requiresAdminUser?: boolean;
  requiresTeamContext?: boolean;
  isDesktopOnly?: boolean;
  openNewTab?: boolean;
  miniText?: string;
};

export const menuData = (
  user: ExtendedThalamosUser | null,
): ThalamosMenuItems[] => {
  const isGuestUser = isGuestUserSession(user);

  const pathname = window.location.pathname;
  const regex = /\/patients\/([a-zA-Z0-9-]+)/;
  const match = pathname.match(regex);

  const isManagerRole = isManagerUser(user);

  const helpPageUrl =
    user?.sessionOrganisationConfiguration?.helpPageUrl ??
    defaultConfiguration.helpPageUrl;

  const supportPageUrl =
    user?.sessionOrganisationConfiguration?.supportPageUrl ??
    defaultConfiguration.supportPageUrl;

  const isTeamManager = isTeamManagerUser(user);

  const isSoadUser = isSoadUserSession(user);
  const isCqcUser = isCqcUserSession(user);

  const userSessionTeamId = user?.sessionContext?.teamId;

  const isDemographicsManager = isDemographicsManagerUser(user);

  const additionalMenuItems = [
    {
      label: "help guides",
      text: "Help Guides",
      link: helpPageUrl,
      openNewTab: true,
      icon: <MenuBookOutlined strokeWidth={0.8} stroke="white" />,
    },
    {
      label: "support",
      text: "Support",
      link: supportPageUrl,
      openNewTab: true,
      icon: <ContactSupportOutlined strokeWidth={0.8} stroke="white" />,
    },
    {
      label: "terms",
      text: "Terms & Conditions",
      link: "https://www.thalamos.co.uk/terms-and-conditions/",
      miniText: "T & C",
      openNewTab: true,
      icon: <ArticleOutlined strokeWidth={0.8} stroke="white" />,
    },
    {
      label: "logout",
      text: "Logout",
      isDesktopOnly: true,
      miniText: "Logout",
      icon: <LogoutSharp strokeWidth={0.8} stroke="white" />,
    },
  ];
  const demographicsManagerItems = isDemographicsManager
    ? [
        {
          label: "Demographics Management",
          text: "Demographics Management",
          link: routeFns.demographicsManagement(),
          icon: <Person2Outlined strokeWidth={0.8} stroke="white" />,
          requiresTeamContext: false,
        },
      ]
    : [];

  // MCA teams work from the register rather than a caseload, so it replaces the
  // caseload rather than sitting beside it.
  if (isMcaUserSession(user)) {
    return [
      {
        label: "residents",
        text: "Residents",
        link: routeFns.careHomeRegister(),
        icon: <GroupOutlined strokeWidth={0.8} stroke="white" />,
      },
      {
        label: "notifications",
        text: "Notifications",
        link: routeFns.notifications(),
        icon: <NotificationsNoneRounded strokeWidth={0.8} stroke="white" />,
      },
      ...additionalMenuItems,
    ];
  }

  // Payment reviewer teams get a dedicated, stripped-down menu
  const isPaymentReviewer = isPaymentReviewerSession(user);
  if (isPaymentReviewer) {
    return [
      {
        label: "payment-requests",
        text: "Payment Requests",
        link: routeFns.paymentReviewerPage(),
        icon: <PaymentOutlined strokeWidth={0.8} stroke="white" />,
      },
      ...additionalMenuItems,
    ];
  }

  const canMakePaymentRequests =
    hasDoctorGmcNumber(user) &&
    !!user?.sessionOrganisationConfiguration &&
    canDoctorInitiateS12Payments(user.sessionOrganisationConfiguration);

  const items = isManagerRole
    ? [
        ...(!isTeamManager
          ? [
              {
                label: "Users",
                text: "Users",
                link: routeFns.managerUsersPage(),
                icon: <Person2Outlined strokeWidth={0.8} stroke="white" />,
                requiresTeamContext: false,
              },
            ]
          : []),

        ...(!isTeamManager
          ? [
              {
                label: "Teams",
                text: "Teams",
                link: routeFns.managerTeamsPage(),
                icon: <GroupOutlined strokeWidth={0.8} stroke="white" />,
                requiresTeamContext: false,
              },
            ]
          : userSessionTeamId
            ? [
                {
                  label: "Team",
                  text: "Team",
                  link: routeFns.managerTeamEdit(userSessionTeamId),
                  icon: <GroupOutlined strokeWidth={0.8} stroke="white" />,
                  requiresTeamContext: false,
                },
              ]
            : []),
        ...additionalMenuItems,
      ]
    : [
        ...demographicsManagerItems,
        {
          label: "admin",
          text: "Admin",
          miniText: "Admin",
          link: routeFns.adminUsersPage(),
          requiresAdminUser: true,
          icon: <SettingsOutlined strokeWidth={0.8} stroke="white" />,
        },
        {
          // Internal-only analytics workspace; opens in its own window (a
          // distinct, chromeless tool — see App.tsx / PathwayWorkspace).
          label: "pathway-analytics",
          text: "Pathway Analytics",
          miniText: "Pathway",
          link: routeFns.pathwayAnalytics(),
          requiresAdminUser: true,
          openNewTab: true,
          icon: <TimelineOutlined strokeWidth={0.8} stroke="white" />,
        },
        {
          // Internal-only connectivity map; opens in its own window (a distinct,
          // chromeless tool — see App.tsx / NetworkMapWorkspace).
          label: "network-map",
          text: "Network Map",
          miniText: "Network",
          link: routeFns.networkMap(),
          requiresAdminUser: true,
          openNewTab: true,
          icon: <HubOutlined strokeWidth={0.8} stroke="white" />,
        },
        {
          label: "caseload",
          text: "Caseload",
          link: routeFns.caseloadCaseload(),
          icon: <Inventory2Outlined strokeWidth={0.8} stroke="white" />,
          requiresTeamContext: true,
        },
        ...(canViewPatientList(user)
          ? [
              {
                label: "patient-list",
                text: "Patient list",
                link: routeFns.patientList(),
                icon: (
                  <PeopleOutlineOutlined strokeWidth={0.8} stroke="white" />
                ),
                requiresTeamContext: true,
                disabled: isGuestUser,
              },
            ]
          : []),
        ...(canMakePaymentRequests
          ? [
              {
                label: "payment-requests",
                text: "Payment Requests",
                link: routeFns.myPaymentRequestsPage(),
                icon: <PaymentOutlined strokeWidth={0.8} stroke="white" />,
              },
            ]
          : []),
        ...(!isSoadUser && !isCqcUser
          ? [
              {
                label: "Patient",
                text: "Search Patient",
                link: routeFns.patientSearch(),
                icon: <Person2Outlined strokeWidth={0.8} stroke="white" />,
                requiresTeamContext: isDemographicsManager ? false : true,
                disabled: isGuestUser,
              },
            ]
          : []),
        ...(!isSoadUser &&
        !isNHSDashboardViewerUser(user) &&
        !isSupersetDashboardViewerUser(user)
          ? [
              {
                label: "dashboard",
                link: routeFns.dashboard(),
                text: "Dashboard",
                icon: <DashboardOutlined strokeWidth={0.8} stroke="white" />,
                disabled: isGuestUser,
              },
            ]
          : []),
        ...(isNHSDashboardViewerUser(user)
          ? [
              {
                label: "NHS London 136 Dashboard",
                link: routeFns.nhsDashboard(),
                text: "NHS London 136 Dashboard",
                icon: <DashboardOutlined strokeWidth={0.8} stroke="white" />,
                disabled: false,
              },
            ]
          : []),
        ...(isSupersetDashboardViewerUser(user)
          ? [
              {
                label: "Superset Dashboard",
                link: routeFns.dashboard(),
                text: "Superset Dashboard",
                icon: <DashboardOutlined strokeWidth={0.8} stroke="white" />,
                disabled: false,
              },
            ]
          : []),
        {
          label: "notifications",
          text: "Notifications",
          link: routeFns.notifications(),
          icon: <NotificationsNoneRounded strokeWidth={0.8} stroke="white" />,
          disabled: isGuestUser,
          requiresTeamContext: true,
        },
        ...additionalMenuItems,
      ];

  if (match) {
    const patientId = match[1];
    if (patientId && !isGuestUser) {
      items.unshift({
        label: "newForm",
        text: "New Form",
        link: routeFns.patientHome(patientId),
        icon: <Add strokeWidth={0.8} stroke="white" />,
        requiresTeamContext: true,
      });
    }
  }

  return items;
};

export const mobileMenuData = (
  user: ExtendedThalamosUser | null,
): ThalamosMenuItems[] => {
  const isGuestUser = isGuestUserSession(user);

  return [
    ...menuData(user),
    {
      label: "settings",
      link: user ? routeFns.userProfilePage(user.id) : "/",
      text: "Settings",
      disabled: isGuestUser,
      icon: <Settings strokeWidth={0.8} stroke="white" />,
    },
    {
      label: "logout",
      link: `https://${config.auth0.domain}/v2/logout?client_id=${
        config.auth0.clientId
      }&returnTo=${encodeURIComponent(
        `${config.backendUrl}/api/oauth/logout`,
      )}`,
      text: "Logout",
      icon: <LogoutSharp strokeWidth={0.8} stroke="white" />,
    },
  ];
};
